"use client";

import styles from "../../common/Dialog/Dialog.module.scss";
import { ForwardButton } from "../../common/Buttons/ForwardButton/ForwardButton";
import { Step } from "../../common/Dialog/Step/Step";
import { useCallback, useEffect, useState } from "react";
import { StepFour } from "./StepFour";
import { BackButton } from "../../common/Buttons/BackButton/BackButton";
import { Choice } from "../../common/Buttons/Choice/Choice";
import { CloseButton } from "../../common/Buttons/CloseButton/CloseButton";
import { ButtonSubmit } from "../../common/Dialog/ButtonSubmit/ButtonSubmit";
import {
  getFormPostulationTexts,
  sendFormContact,
} from "@/utils/fetch-api-dialog-postulation";
import { useLocale } from "next-intl";
import { FormTexts } from "@/types/types";
import { validateEmail } from "@/utils/regex";
import DialogScene from "@/components/common/Dialog/DialogScene";
import { useScrollStore } from "@/components/common/ScrollManager/useScrollStore";
import { PixelEvents } from "@/components/Pixel/PixelManager";

export const DialogPostulation = ({
  isContactOpen,
  setIsContactOpen,
  appliedPosition = "",
}) => {
  const { scroller } = useScrollStore();
  const locale = useLocale();
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [active, setActive] = useState(1);
  const [isDisable, setIsDisable] = useState(true);
  const [errorFormData, setErrorFormData] = useState({});
  const [filledCounts, setFilledCounts] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [formInputData, setFormInputData] = useState({
    appliedPosition: "",
    applicantName: "",
    applicantEmail: "",
    description: "",
    expected_income: "",
    cv: "",
  });
  const [messages, setMessages] = useState<FormTexts>({ steps: [] });

  useEffect(() => {
    setFormInputData((prevForm) => ({
      ...prevForm,
      appliedPosition: appliedPosition,
    }));
    setSelectedOptions((prevOptions) => [
      appliedPosition,
      ...prevOptions.slice(1),
    ]);
  }, [appliedPosition]);

  useEffect(() => {
    const fetchFormTexts = async () => {
      setIsLoading(true);
      const texts = await getFormPostulationTexts(locale);
      setMessages(texts);
      setIsLoading(false);
    };
    fetchFormTexts();
  }, [locale]);

  useEffect(() => {
    const handleBodyOverflow = () => {
      if (isContactOpen) {
        scroller?.stop();
        // Track inicio de postulación
        PixelEvents.startJobApplication(appliedPosition);
      } else {
        scroller?.start();
      }
    };

    handleBodyOverflow();

    return () => {
      scroller?.start();
    };
  }, [isContactOpen]);

  useEffect(() => {
    const isSelectionValid = selectedOptions.length >= active;

    const hasValidOptions = (stepChoices: string[] = []) =>
      stepChoices.some((option) => selectedOptions.includes(option));

    const steps = messages.steps;
    const stepChoices = steps[active - 1]?.choices || [];

    setIsDisable(!isSelectionValid || !hasValidOptions(stepChoices));
  }, [active, selectedOptions, messages]);

  useEffect(() => {
    const count = Object.values(formInputData).filter(
      (value) => value !== ""
    ).length;
    setFilledCounts(count);
  }, [formInputData, setFilledCounts]);

  const handleChangeInput = (source, value) => {
    setFormInputData((prevForm) => ({
      ...prevForm,
      [source]: value,
    }));
    
    // Track cuando se sube el CV
    if (source === 'cv' && value) {
      PixelEvents.uploadCV(formInputData.appliedPosition);
    }
  };

  const handleNext = useCallback(() => {
    setActive((prev: any) => prev + 1);
  }, [setActive]);

  const handleBack = useCallback(() => {
    if (active <= 1) return;
    setActive(active - 1);
  }, [setActive, active]);
  const stepChoices = (step) => {
    const filteredOptions =
      messages.steps[step - 1]?.choices?.filter((option) =>
        selectedOptions.includes(option)
      ) || [];

    return filteredOptions.map((option) => ({
      type: "paragraph",
      children: [{ type: "text", text: option }],
    }));
  };
  const sendForm = async () => {
    let errors = {};
    if (active === 4) {
      if (formInputData.applicantName.trim() === "") {
        errors = { ...errors, applicantName: "Complete el nombre" };
      }
      if (formInputData.applicantEmail.trim() === "") {
        errors = { ...errors, applicantEmail: "Complete el email" };
      } else if (!validateEmail(formInputData.applicantEmail)) {
        errors = { ...errors, applicantEmail: "Email inválido" };
      }
      if (formInputData.description.trim() === "") {
        errors = { ...errors, comment: "Complete la descripción" };
      }
      if (formInputData.expected_income.trim() === "") {
        errors = { ...errors, expected_income: "Complete el valor en pesos" };
      }
      if (!formInputData.cv) {
        errors = { ...errors, cv: "Debes agrega tu CV" };
      }
    }
    if (Object.values(errors).length > 0) {
      setErrorFormData(errors);
    } else {
      setIsLoading(true);

      const payload = {
        data: {
          ...formInputData,
          answers: [
            {
              id: 1,
              title: messages.steps[0]?.title,
              description: "",
              choices: stepChoices(1),
            },
            {
              id: 2,
              title: messages.steps[1]?.title,
              description: "",
              choices: stepChoices(2),
            },
            {
              id: 3,
              title: messages.steps[2]?.title,
              description: "",
              choices: stepChoices(3),
            },
          ],
        },
      };
      try {
        const response = await sendFormContact(payload);
        if (!response) {
          setError("Error al enviar el formulario");
        } else {
          // Track envío exitoso de postulación en todos los pixels activos
          PixelEvents.submitJobApplication(
            formInputData.appliedPosition,
            formInputData.expected_income
            // 'TU_CONVERSION_ID' // Tercer parámetro opcional para LinkedIn
          );
          setActive(5);
        }
      } catch (error) {
        setError("Error al enviar el formulario");
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div
      className={`${styles.overlayContainer} ${
        isContactOpen ? styles.active : ""
      }`}
    >
      <div className={styles.overlay}>
        <div
          className={`${styles.dialogContainer} ${active === 5 ? styles.stepFiveGap : null} ${
            active === 5 ? styles.stepFiveHeight : null
          }`}
        >
          <div className={styles.choicesQuestions}>
            <div className={styles.choicesClose}>
              {active !== 5 && <Choice choice={selectedOptions} />}
              <CloseButton setIsContactOpen={setIsContactOpen} />
            </div>
            <div className={styles.step}>
              {active === 1 && (
                <div className={`${styles.dialogContent} ${styles.active}`}>
                  <Step
                    title={messages.steps[0]?.title}
                    description={messages.steps[0]?.description}
                    choices={messages.steps[0]?.choices}
                    setSelectedOptions={setSelectedOptions}
                    selectedOptions={selectedOptions}
                  />
                </div>
              )}
              {active === 2 && (
                <div className={`${styles.dialogContent} ${styles.active}`}>
                  <Step
                    title={messages.steps[1]?.title}
                    description={messages.steps[1]?.description}
                    choices={messages.steps[1]?.choices}
                    setSelectedOptions={setSelectedOptions}
                    selectedOptions={selectedOptions}
                  />
                </div>
              )}
              {active === 3 && (
                <div className={`${styles.dialogContent} ${styles.active}`}>
                  <Step
                    title={messages.steps[2]?.title}
                    description={messages.steps[2]?.description}
                    choices={messages.steps[2]?.choices}
                    setSelectedOptions={setSelectedOptions}
                    selectedOptions={selectedOptions}
                  />
                </div>
              )}
              {active === 4 && (
                <div className={`${styles.dialogContent} ${styles.active}`}>
                  <StepFour
                    messages={messages.steps[3]}
                    setErrorFormData={setErrorFormData}
                    errorFormData={errorFormData}
                    formData={formInputData}
                    handleInputChange={handleChangeInput}
                  />
                </div>
              )}
            </div>
          </div>

          <div className={styles.navigationBtnContainer}>
            {active > 1 && active <= 4 && (
              <BackButton handleClick={handleBack} />
            )}
            {active < 4 ? (
              <ForwardButton disabled={isDisable} onClick={handleNext} />
            ) : active === 4 ? (
              <ButtonSubmit
                filledCounts={filledCounts}
                isLoading={isLoading}
                sendForm={sendForm}
                limit={6}
              />
            ) : null}
          </div>

          {active === 5 && (
            <div
              className={`${styles.canvas} ${active === 5 ? styles.active : ""}`}
            >
              <div className={styles.canvasText}>
                <h2 className={styles.canvasTitle}>{messages.steps[4]?.title}</h2>
                <p className={styles.canvasDescription}>{messages.steps[4]?.description}</p>
              </div>
              <div
                className={` ${styles.canvas} ${
                  active === 5 ? styles.active : ""
                }`}
              >
                <DialogScene />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
